/* ============================================================
   F.G. EDWARDS - Journees poules PDP behaviour
   Powers jp-pdp-* sections (product.poule.json). Every handler is
   guarded so a section missing from the page is simply skipped.
   Behaviours:
     - event band: live deadline countdown (days/heures/min) + GA view
     - gallery thumb swap (alt-text preserved by the Liquid)
     - buy box: store cards -> date chips -> qty -> required feed
       selector -> reserve (bird + feed in one /cart/add.js items[]),
       cart-drawer re-render, ?variant sync, localStorage same-flock
       memory, sticky mobile bar, final-sale disclosure, priority
       waitlist capture (Klaviyo _klOnsite + localStorage)
     - GA4 (guarded): jp_event_band_view, jp_store_select, jp_date_select,
       jp_feed_addon_view, jp_reserve, jp_waitlist_signup
   The buy flow reuses fg-fastbuy internals (AJAX ATC, qty rules,
   restock) around a reservation-specific selector.
   ============================================================ */
(function () {
  'use strict';

  var STORAGE_KEY = 'jp-event-selection';

  function ga(name, params) {
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', name, params || {}); } catch (e) { /* noop */ }
    }
  }

  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
  }

  /* ---------- event band (countdown + GA view) ---------- */
  function initEventBand() {
    var band = document.querySelector('[data-jp-event-band]');
    if (!band) return;
    if (band.dataset.jpBandInit) return;
    band.dataset.jpBandInit = '1';

    // GA4 jp_event_band_view, once, when the band scrolls into view.
    var gaName = band.getAttribute('data-jp-band-ga') || 'jp_event_band_view';
    var state = (band.className.match(/jpp-eventband--(\w+)/) || [])[1] || '';
    var fired = false;
    function fire() { if (fired) return; fired = true; ga(gaName, { state: state }); }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { fire(); io.disconnect(); }
        });
      }, { threshold: 0.4 });
      io.observe(band);
    } else {
      fire();
    }

    // Live countdown to the reservation deadline (days / heures / min).
    var cd = band.querySelector('[data-jp-band-countdown]');
    if (!cd) return;
    var iso = cd.getAttribute('data-jp-deadline');
    if (!iso) return;
    var end = Date.parse(iso);
    if (isNaN(end)) return;
    var timer = cd.querySelector('[data-jp-band-timer]');
    var elD = cd.querySelector('[data-jp-band-days]');
    var elH = cd.querySelector('[data-jp-band-hours]');
    var elM = cd.querySelector('[data-jp-band-mins]');

    function tick() {
      var diff = end - Date.now();
      if (diff <= 0) {
        if (timer) timer.hidden = true;
        cd.classList.add('is-expired');
        return false;
      }
      var s = Math.floor(diff / 1000);
      var d = Math.floor(s / 86400); s -= d * 86400;
      var h = Math.floor(s / 3600); s -= h * 3600;
      var m = Math.floor(s / 60);
      if (elD) elD.textContent = d;
      if (elH) elH.textContent = h;
      if (elM) elM.textContent = m;
      if (timer) timer.hidden = false;
      if (diff < 86400000) cd.classList.add('is-urgent');
      return true;
    }
    if (tick() === false) return;
    var t = setInterval(function () { if (tick() === false) clearInterval(t); }, 60000);
  }

  /* ---------- gallery ---------- */
  function initGallery() {
    var gallery = document.querySelector('[data-jp-gallery]');
    if (!gallery) return;
    var thumbs = gallery.querySelectorAll('[data-jp-thumb]');
    var media = gallery.querySelectorAll('[data-jp-media-index]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = thumb.getAttribute('data-jp-thumb');
        media.forEach(function (m) { m.hidden = m.getAttribute('data-jp-media-index') !== idx; });
        thumbs.forEach(function (t) { t.classList.remove('jpp-hero__thumb--active'); });
        thumb.classList.add('jpp-hero__thumb--active');
      });
    });
  }

  /* ---------- money ---------- */
  function makeMoneyFormatter(format) {
    format = (format || '${{amount}}')
      .replace(/&quot;/g, '"').replace(/&#123;/g, '{').replace(/&#125;/g, '}').replace(/&amp;/g, '&');
    return function (cents) {
      if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
      var dollars = (cents / 100).toFixed(2);
      var noDec = Math.round(cents / 100).toString();
      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ' '))
        .replace(/\{\{\s*amount\s*\}\}/g, dollars);
    };
  }

  /* ---------- buy box ---------- */
  function initBuybox() {
    var root = document.querySelector('[data-jp-buybox]');
    if (!root) return;

    var money = makeMoneyFormatter(root.getAttribute('data-money-format'));
    var maxOrderQty = parseInt(root.getAttribute('data-max-order-qty'), 10) || 0;
    var feedRequired = root.getAttribute('data-feed-required') === 'true';
    var feedTagSatisfied = root.getAttribute('data-feed-tag-satisfied') === 'true';

    function parseJSON(sel, fallback) {
      var el = root.querySelector(sel);
      if (!el) return fallback;
      try { return JSON.parse(el.textContent || ''); } catch (e) { return fallback; }
    }
    var variants = parseJSON('[data-jp-variants]', []);

    /* --- substitution: the closest breed still in stock --- */
    /* Egg colour first (compared in CIE Lab, as the quiz does), then the
       four temperament scores. `store` narrows to breeds with a bird left
       at that store; `day` prefers the same pickup day. */
    var subs = parseJSON('[data-jp-subs]', null);
    function subLab(hex) {
      var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
      if (!m) return null;
      var c = [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)].map(function (v) {
        v /= 255; return v > 0.04045 ? Math.pow((v + 0.055) / 1.055, 2.4) : v / 12.92;
      });
      var x = (c[0] * 0.4124 + c[1] * 0.3576 + c[2] * 0.1805) / 0.95047;
      var y = (c[0] * 0.2126 + c[1] * 0.7152 + c[2] * 0.0722);
      var z = (c[0] * 0.0193 + c[1] * 0.1192 + c[2] * 0.9505) / 1.08883;
      function f(t) { return t > 0.008856 ? Math.pow(t, 1 / 3) : (7.787 * t + 16 / 116); }
      return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
    }
    function subLikeness(a, b) {
      var la = subLab(a.hex); var lb = subLab(b.hex);
      var d = 0.6;
      if (la && lb) {
        var dl = (la[0] - lb[0]) * 0.5, da = la[1] - lb[1], db = la[2] - lb[2];
        d = Math.min(1, Math.sqrt(dl * dl + da * da + db * db) / 60) * 2;
      }
      ['kid', 'doc', 'cold', 'noise'].forEach(function (k) {
        var x = parseFloat(a[k]); var y = parseFloat(b[k]);
        d += (isNaN(x) || isNaN(y)) ? 0.2 : Math.abs(x - y) / 5;
      });
      return d;
    }
    function closestBreed(store, day) {
      if (!subs || !subs.others || !subs.others.length) return null;
      var best = null; var bestD = Infinity;
      subs.others.forEach(function (o) {
        var vs = (o.v || []).filter(function (v) { return !store || v.s === store; });
        if (!vs.length) return;
        var d = subLikeness(subs.me || {}, o);
        if (day && !vs.some(function (v) { return v.d === day; })) d += 0.5; /* another day: less good */
        if (d < bestD) { bestD = d; best = o; }
      });
      return best;
    }
    function fillSub(el, store, day) {
      if (!el) return;
      var b = closestBreed(store, day);
      el.textContent = '';
      if (!b) { el.hidden = true; return; }
      el.appendChild(document.createTextNode((subs.label || 'Semblable et disponible :') + ' '));
      var a = document.createElement('a');
      a.href = b.url;
      a.textContent = b.title;
      a.addEventListener('click', function () { ga('jp_substitute_click', { to: b.title, store: store || '' }); });
      el.appendChild(a);
      el.hidden = false;
    }
    var subInline = root.querySelector('[data-jp-sub-inline]');
    fillSub(root.querySelector('[data-jp-sub]'), null, null); /* whole breed sold out */
    var pickupDates = parseJSON('[data-jp-pickup-dates]', {});
    var qtyRules = parseJSON('[data-jp-qty-rules]', {});
    var feedVariantIds = parseJSON('[data-jp-feed-variant-ids]', []).map(String);

    var els = {
      stores: root.querySelectorAll('.jpp-store[data-jp-store]'),
      datesWrap: root.querySelector('[data-jp-dates]'),
      dateChips: [].slice.call(root.querySelectorAll('[data-jp-date]')),
      qtyInput: root.querySelector('[data-jp-qty-input]'),
      qtyMinus: root.querySelector('[data-jp-qty-minus]'),
      qtyPlus: root.querySelector('[data-jp-qty-plus]'),
      feedWrap: root.querySelector('[data-jp-feed]'),
      feedInputs: root.querySelectorAll('[data-jp-feed-input]'),
      feedConfirm: root.querySelector('[data-jp-feed-confirm]'),
      feedStep: root.querySelector('[data-jp-step-feed]'),
      total: root.querySelector('[data-jp-total]'),
      form: root.querySelector('[data-jp-form]'),
      formVariant: root.querySelector('[data-jp-form-variant]'),
      formQty: root.querySelector('[data-jp-form-qty]'),
      atc: root.querySelector('[data-jp-atc]'),
      atcText: root.querySelector('[data-jp-atc-text]'),
      atcLoading: root.querySelector('[data-jp-atc-loading]'),
      atcSuccess: root.querySelector('[data-jp-atc-success]'),
      msg: root.querySelector('[data-jp-msg]'),
      disclosureToggle: root.querySelector('[data-jp-disclosure-toggle]'),
      disclosureBody: root.querySelector('[data-jp-disclosure-body]'),
      stickyPrice: document.querySelector('[data-jp-sticky-price]'),
      stickyBtn: document.querySelector('[data-jp-sticky-btn]')
    };

    var reserveTmpl = (els.atcText && els.atcText.getAttribute('data-jp-reserve-tmpl')) ||
      'Réserver pour le {date}';

    var state = {
      store: null,
      variantId: els.formVariant ? String(els.formVariant.value) : null,
      quantity: parseInt(els.formQty && els.formQty.value, 10) || 1,
      feedVariantId: null,
      feedPrice: 0,
      feedSatisfied: feedTagSatisfied
    };

    function findVariant(id) {
      id = String(id);
      for (var i = 0; i < variants.length; i++) { if (String(variants[i].id) === id) return variants[i]; }
      return null;
    }
    function currentVariant() { return findVariant(state.variantId); }

    function getQtyRule(id) {
      var rule = qtyRules[id] || qtyRules[String(id)] || {};
      var max = 9999;
      if (maxOrderQty > 0) max = maxOrderQty; else if (rule.max) max = rule.max;
      return { min: rule.min || 1, max: max, increment: rule.increment || 1 };
    }

    /* --- chip ordering by pickup_date, else DOM order --- */
    function orderChips() {
      if (!els.datesWrap) return;
      els.dateChips.forEach(function (chip, i) { chip.setAttribute('data-jp-order', i); });
      var sorted = els.dateChips.slice().sort(function (a, b) {
        var da = pickupDates[a.getAttribute('data-jp-variant-id')] || '';
        var db = pickupDates[b.getAttribute('data-jp-variant-id')] || '';
        if (da && db && da !== db) return da < db ? -1 : 1;
        if (da && !db) return -1;
        if (!da && db) return 1;
        return (+a.getAttribute('data-jp-order')) - (+b.getAttribute('data-jp-order'));
      });
      sorted.forEach(function (chip) { els.datesWrap.appendChild(chip); });
      els.dateChips = sorted;
    }

    /* --- total --- */
    function updateTotal() {
      var variant = currentVariant();
      if (!variant) return;
      var sum = variant.price * state.quantity;
      if (feedRequired && !state.feedSatisfied) sum += state.feedPrice;
      if (els.total) els.total.textContent = money(sum);
      if (els.stickyPrice) els.stickyPrice.textContent = money(sum);
      syncQtyPresets();
    }

    /* --- broiler quantity presets --- */
    var qtyPresets = [].slice.call(root.querySelectorAll('[data-jp-qty-preset]'));
    function syncQtyPresets() {
      qtyPresets.forEach(function (b) {
        b.classList.toggle('is-selected', parseInt(b.getAttribute('data-jp-qty-preset'), 10) === state.quantity);
      });
    }

    /* --- reserve label --- */
    function updateReserveLabel() {
      var variant = currentVariant();
      if (!variant || !els.atcText) return;
      var date = variant.option2 || variant.title || '';
      els.atcText.textContent = reserveTmpl.replace('{date}', date);
    }

    /* --- quantity --- */
    function clampQty() {
      var rule = getQtyRule(state.variantId);
      if (state.quantity > rule.max) state.quantity = rule.max;
      if (state.quantity < rule.min) state.quantity = rule.min;
      if (els.qtyInput) {
        els.qtyInput.value = state.quantity;
        els.qtyInput.min = rule.min;
        els.qtyInput.max = rule.max;
        els.qtyInput.step = rule.increment;
      }
      if (els.formQty) els.formQty.value = state.quantity;
    }
    function stepQty(dir) {
      var rule = getQtyRule(state.variantId);
      state.quantity = state.quantity + rule.increment * dir;
      clampQty();
      updateTotal();
    }

    /* --- selection --- */
    function persist() {
      var variant = currentVariant();
      if (!variant) return;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ store: variant.option1, date: variant.option2 }));
      } catch (e) { /* noop */ }
    }

    function selectDate(variantId, silent) {
      var variant = findVariant(variantId);
      if (!variant || !variant.available) return;
      state.variantId = String(variantId);
      els.dateChips.forEach(function (chip) {
        chip.classList.toggle('is-selected', chip.getAttribute('data-jp-variant-id') === String(variantId));
      });
      if (els.formVariant) els.formVariant.value = variantId;
      clampQty();
      updateReserveLabel();
      updateTotal();
      var stockLine = root.querySelector('[data-jp-qty-stock]');
      var stockN = root.querySelector('[data-jp-qty-stock-n]');
      if (stockLine && stockN) {
        if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0) {
          stockN.textContent = variant.inventory_quantity;
          stockLine.hidden = false;
        } else {
          stockLine.hidden = true;
        }
      }
      // ?variant sync
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* noop */ }
      persist();
      if (!silent) ga('jp_date_select', { store: variant.option1, date: variant.option2, variant_id: variantId });
    }

    function selectStore(store, preferredDate, silent) {
      state.store = store;
      els.stores.forEach(function (btn) {
        var on = btn.getAttribute('data-jp-store') === store;
        btn.classList.toggle('is-selected', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      // filter chips to this store
      var firstAvailable = null;
      var preferred = null;
      els.dateChips.forEach(function (chip) {
        var match = chip.getAttribute('data-jp-store') === store;
        chip.hidden = !match;
        if (match && !chip.disabled) {
          if (!firstAvailable) firstAvailable = chip;
          if (preferredDate && chip.getAttribute('data-jp-date-label') === preferredDate) preferred = chip;
        }
      });
      var pick = preferred || firstAvailable;
      if (subInline) subInline.hidden = true;
      if (pick) {
        if (els.atc) els.atc.disabled = false;
        selectDate(pick.getAttribute('data-jp-variant-id'), silent);
      } else {
        // Store has no available variant: never leave a stale variant armed.
        state.variantId = null;
        if (els.formVariant) els.formVariant.value = '';
        els.dateChips.forEach(function (chip) { chip.classList.remove('is-selected'); });
        if (els.atc) els.atc.disabled = true;
        updateTotal();
        if (!silent) showMsg(root.getAttribute('data-jp-store-full-msg') || 'Complet à ce magasin pour cette journée.');
        fillSub(subInline, store, preferredDate || null);
      }
      if (!silent) ga('jp_store_select', { store: store });
    }

    /* --- feed --- */
    function collapseFeed() {
      state.feedSatisfied = true;
      if (els.feedWrap) els.feedWrap.hidden = true;
      if (els.feedConfirm) els.feedConfirm.hidden = false;
      updateTotal();
    }
    function selectFeed(input) {
      state.feedVariantId = input.value;
      state.feedPrice = parseInt(input.getAttribute('data-jp-feed-price'), 10) || 0;
      els.feedInputs.forEach(function (i) {
        var card = i.closest('.jpp-feed__card');
        if (card) card.classList.toggle('is-selected', i === input);
      });
      updateTotal();
    }
    function initFeed() {
      if (!feedRequired) return;
      // seed from the pre-checked first radio
      var checked = null;
      els.feedInputs.forEach(function (i) { if (i.checked) checked = i; });
      if (!checked && els.feedInputs.length) { checked = els.feedInputs[0]; checked.checked = true; }
      if (checked) selectFeed(checked);
      els.feedInputs.forEach(function (i) {
        i.addEventListener('change', function () { selectFeed(i); });
      });

      if (feedTagSatisfied) { collapseFeed(); return; }

      // Collapse when the cart already holds a qualifying feed line
      fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var items = (cart && cart.items) || [];
          var found = items.some(function (li) { return feedVariantIds.indexOf(String(li.variant_id)) !== -1; });
          if (found) collapseFeed(); else ga('jp_feed_addon_view', {});
        })
        .catch(function () { ga('jp_feed_addon_view', {}); });
    }

    /* --- reserve (AJAX, items[]) --- */
    function showMsg(text) {
      if (!els.msg) return;
      els.msg.textContent = text;
      els.msg.className = 'jpp-buybox__msg is-visible is-error';
      setTimeout(function () { els.msg.classList.remove('is-visible'); }, 5000);
    }
    function setLoading(on) {
      if (els.atcText) els.atcText.hidden = on;
      if (els.atcLoading) els.atcLoading.hidden = !on;
      els.atc.classList.toggle('is-loading', on);
      els.atc.disabled = on;
    }
    function showSuccess() {
      if (els.atcLoading) els.atcLoading.hidden = true;
      if (els.atcSuccess) els.atcSuccess.hidden = false;
      els.atc.classList.remove('is-loading');
      els.atc.classList.add('is-success');
      setTimeout(function () {
        els.atc.classList.remove('is-success');
        els.atc.disabled = false;
        if (els.atcSuccess) els.atcSuccess.hidden = true;
        if (els.atcText) els.atcText.hidden = false;
      }, 2000);
    }

    function reserve() {
      var variant = currentVariant();
      if (!variant || !variant.available) {
        showMsg(root.getAttribute('data-jp-store-full-msg') || 'Cette combinaison est complète.');
        return;
      }

      var items = [{ id: parseInt(state.variantId, 10), quantity: state.quantity }];
      if (feedRequired && !state.feedSatisfied && state.feedVariantId) {
        items.push({ id: parseInt(state.feedVariantId, 10), quantity: 1 });
      }

      var body = { items: items };
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        body.sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
        body.sections_url = window.location.pathname;
        if (typeof cartDrawer.setActiveElement === 'function') cartDrawer.setActiveElement(document.activeElement);
      }

      setLoading(true);
      var controller = new AbortController();
      var timeout = setTimeout(function () { controller.abort(); }, 8000);
      var addUrl = (window.routes && window.routes.cart_add_url) || (shopRoot() + 'cart/add.js');

      fetch(addUrl, {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
        .then(function (r) {
          clearTimeout(timeout);
          return r.json().then(function (data) { return { ok: r.ok, data: data }; });
        })
        .then(function (res) {
          if (!res.ok || (res.data && res.data.status && res.data.status !== 200)) {
            throw new Error((res.data && (res.data.description || res.data.message)) || 'Erreur');
          }
          setLoading(false);
          showSuccess();
          ga('jp_reserve', {
            variant_id: state.variantId,
            quantity: state.quantity,
            feed_variant_id: (feedRequired && !state.feedSatisfied) ? state.feedVariantId : null
          });
          if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'jp-buybox', productVariantId: state.variantId });
          }
          if (cartDrawer) {
            if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
            if (typeof cartDrawer.renderContents === 'function') cartDrawer.renderContents(res.data);
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: { id: state.variantId, quantity: state.quantity } }
          }));
        })
        .catch(function (err) {
          clearTimeout(timeout);
          setLoading(false);
          if (err && err.name === 'AbortError') {
            showMsg('La demande a expiré. Veuillez réessayer.');
          } else {
            showMsg((err && err.message) || 'Erreur lors de la réservation.');
          }
        });
    }

    /* --- priority waitlist capture (off-season / sold out) --- */
    function initRestock() {
      var wrap = root.querySelector('[data-jp-restock]');
      var form = root.querySelector('[data-jp-restock-form]');
      if (!form) return;
      var klaviyoList = (wrap && wrap.getAttribute('data-jp-klaviyo-list')) || '';
      var klaviyoKey = (wrap && wrap.getAttribute('data-jp-klaviyo-key')) || '';
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var emailEl = root.querySelector('[data-jp-restock-email]');
        var msgEl = root.querySelector('[data-jp-restock-msg]');
        var email = emailEl && emailEl.value.trim();
        if (!email) return;
        var phoneEl = root.querySelector('[data-jp-restock-phone]');
        var consentEl = root.querySelector('[data-jp-restock-sms-consent]');
        var phone = phoneEl ? phoneEl.value.replace(/[^\d+]/g, '') : '';
        var smsConsent = !!(consentEl && consentEl.checked && phone);
        try {
          var reqs = JSON.parse(localStorage.getItem('fg-restock-requests') || '[]');
          reqs.push({ email: email, phone: smsConsent ? phone : null, sms: smsConsent, productId: root.getAttribute('data-product-id'), list: klaviyoList, timestamp: Date.now() });
          localStorage.setItem('fg-restock-requests', JSON.stringify(reqs));
        } catch (e2) { /* noop */ }
        // Direct Klaviyo client subscribe when the public key + list are
        // configured; SMS consent is only ever sent when the box is checked
        // (CASL express consent, never pre-checked).
        if (klaviyoKey && klaviyoList) {
          var subs = { email: { marketing: { consent: 'SUBSCRIBED' } } };
          var attrs = { email: email };
          if (smsConsent) {
            var e164 = phone.charAt(0) === '+' ? phone : (phone.length === 10 ? '+1' + phone : '+' + phone);
            attrs.phone_number = e164;
            subs.sms = { marketing: { consent: 'SUBSCRIBED' } };
          }
          fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(klaviyoKey), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: { profile: { data: { type: 'profile', attributes: { email: attrs.email, phone_number: attrs.phone_number, subscriptions: subs } } } },
                relationships: { list: { data: { type: 'list', id: klaviyoList } } }
              }
            })
          }).catch(function () { /* localStorage + _klOnsite remain the fallback */ });
        } else if (window._klOnsite) {
          try { window._klOnsite.push(['openForm', klaviyoList || 'BACK_IN_STOCK']); } catch (e3) { /* noop */ }
        }
        ga('jp_waitlist_signup', {
          product_id: root.getAttribute('data-product-id'),
          list_id: klaviyoList || null,
          sms: smsConsent
        });
        if (msgEl) {
          msgEl.textContent = 'Vous êtes sur la liste prioritaire. Nous vous écrirons avant l’ouverture.';
          msgEl.classList.add('is-visible');
        }
        form.style.display = 'none';
      });
    }

    /* --- sticky bar --- */
    function initSticky() {
      var sticky = document.querySelector('[data-jp-sticky]');
      if (!sticky || !els.atc) return;
      if (els.stickyBtn) {
        els.stickyBtn.addEventListener('click', function () {
          if (els.atc.disabled) return;
          reserve();
        });
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var off = !entry.isIntersecting;
            sticky.classList.toggle('is-visible', off);
            sticky.setAttribute('aria-hidden', off ? 'false' : 'true');
          });
        }, { threshold: 0 });
        io.observe(els.atc);
      }
    }

    /* --- bind --- */
    function bind() {
      els.stores.forEach(function (btn) {
        btn.addEventListener('click', function () { selectStore(btn.getAttribute('data-jp-store')); });
      });
      els.dateChips.forEach(function (chip) {
        if (chip.disabled) return;
        chip.addEventListener('click', function () { selectDate(chip.getAttribute('data-jp-variant-id')); });
      });
      qtyPresets.forEach(function (btn) {
        btn.addEventListener('click', function () {
          state.quantity = parseInt(btn.getAttribute('data-jp-qty-preset'), 10) || 1;
          clampQty();
          updateTotal();
        });
      });
      if (els.qtyMinus) els.qtyMinus.addEventListener('click', function () { stepQty(-1); });
      if (els.qtyPlus) els.qtyPlus.addEventListener('click', function () { stepQty(1); });
      if (els.qtyInput) {
        els.qtyInput.addEventListener('change', function () {
          state.quantity = parseInt(els.qtyInput.value, 10) || 1;
          clampQty();
          updateTotal();
        });
      }
      if (els.form) {
        els.form.addEventListener('submit', function (e) { e.preventDefault(); reserve(); });
      }
      if (els.disclosureToggle && els.disclosureBody) {
        els.disclosureToggle.addEventListener('click', function () {
          var open = els.disclosureBody.hidden;
          els.disclosureBody.hidden = !open;
          els.disclosureToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
    }

    /* --- boot --- */
    if (!els.dateChips.length) { initRestock(); return; } // off-season / sold out
    orderChips();
    bind();
    initFeed();
    initSticky();

    // Initial selection: localStorage preferred, else current variant's store/date
    var initialVariant = currentVariant() || (variants.length ? variants[0] : null);
    var startStore = initialVariant ? initialVariant.option1 : null;
    var startDate = initialVariant ? initialVariant.option2 : null;
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && saved.store) {
        var savedMatch = variants.filter(function (vv) {
          return vv.available && vv.option1 === saved.store && vv.option2 === saved.date;
        })[0];
        if (savedMatch) { startStore = saved.store; startDate = saved.date; }
      }
    } catch (e) { /* noop */ }
    if (startStore) selectStore(startStore, startDate, true);
  }

  function boot() {
    initEventBand();
    initGallery();
    initBuybox();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
